import { PencilIcon } from "@heroicons/react/outline";
import { useParams } from "react-router-dom";
import { ICellRendererParams } from "ag-grid-enterprise";

import { CustomModel } from "../../types";
import { mapParametersToPromptData } from "../utils";

import { useAuth } from "@/context/auth-context";
import { useDeleteCustomModel } from "@/queries/models/useDeleteCustomModel";
import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { useDeleteInferenceClient, useInferenceClientsList } from "@/queries";
import { ModelParameters } from "@/components/ModelParameters";
import { ContextMenuItems } from "@/components/ui/ContextMenu/types";
import { Action } from "@/components/ui/DropdownMenu";
import { TableColumn } from "@/components/ui/TableV2";
import { useLLMProviderStore } from "@/providers";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

export const useCustomModelsTable = ({
  onEditModel,
}: {
  onEditModel: (model: CustomModel, isViewing?: boolean) => void;
}) => {
  const { store: llmProviderStore } = useLLMProviderStore();
  const { providers } = llmProviderStore;

  const { workspaceId } = useParams();
  const authContext = useAuth();

  const { mutate: deleteModel, isLoading: isDeletingModel } = useDeleteCustomModel(
    authContext?.userToken ?? "",
  );

  const { mutate: deleteInferenceClient } = useDeleteInferenceClient(authContext?.userToken ?? "");

  const { data: inferenceClients } = useInferenceClientsList(
    authContext?.userToken ?? "",
    workspaceId!,
  );

  const columns: TableColumn<CustomModel>[] = [
    {
      id: 1,
      name: "Custom Name",
      header: "Custom Name",
      field: "display_name",
    },
    {
      id: 2,
      name: "Model",
      header: "Model",
      field: "llm_model_name",
      cellRenderer: (params: ICellRendererParams<CustomModel>) => (
        <OverflowDisplayTooltip content={params.data?.llm_model_name}>
          <span>{params.data?.llm_model_name}</span>
        </OverflowDisplayTooltip>
      ),
    },
    {
      id: 3,
      name: "Type",
      header: "Type",
      field: "is_chat",
      cellRenderer: (params: ICellRendererParams<CustomModel>) =>
        params.data?.is_chat ? "Chat" : "Completion",
    },
    {
      id: 4,
      name: "Parameters",
      header: "Parameters",
      field: "params",
      cellRenderer: (params: ICellRendererParams<CustomModel>) =>
        params.data?.params && params.data?.params.length > 0 ? (
          <div className="flex h-full w-fit items-center justify-center gap-2">
            <ModelParameters
              readonly
              promptData={{
                metadata: {
                  model: {
                    name: params.data?.llm_model_name,
                    provider:
                      providers.find(provider => provider.id === params.data?.provider_id)?.name ??
                      "",
                    model_config_display_name: params.data?.display_name,
                    parameters: mapParametersToPromptData(params.data?.params),
                  },
                },
                provider_id: params.data?.provider_id,
              }}
              triggerVariant="outline"
              showIcon={false}
              showTooltip={false}
              triggerLabel="View Parameters"
              showLearnMore={false}
              showGeneralInformation={false}
              isPromptChat={params.data?.is_chat}
            />
          </div>
        ) : (
          "-"
        ),
    },
  ];

  // const onViewModel = (model: CustomModel) => {
  //   const inferenceClient = inferenceClients?.inference_clients.find(
  //     (client) => client.name === model.display_name,
  //   );
  //   const cookies = Object.entries(inferenceClient?.cookies || {}).map(
  //     ([key, value], index) => ({
  //       id: index,
  //       key,
  //       value,
  //     }),
  //   );
  //   const headers = Object.entries(inferenceClient?.headers || {}).map(
  //     ([key, value], index) => ({
  //       id: index,
  //       key,
  //       value,
  //     }),
  //   );
  //   store.setCookies(cookies);
  //   store.setHeaders(headers);
  //   store.setTimeout(inferenceClient?.timeout || 1);
  //   onEditModel(model, true);
  // };

  const onDeleteModel = (model: CustomModel) => {
    const inferenceClient = inferenceClients?.inference_clients.find(
      client => client.name === model.display_name,
    );

    if (inferenceClient) {
      deleteInferenceClient({ id: inferenceClient.id });
    }

    deleteModel(String(model.id));
  };

  const actions: Action<CustomModel>[] = [
    // {
    //   id: "view",
    //   label: "View",
    //   icon: EyeIcon,
    //   onClick: (model) => onViewModel(model!),
    // },
    {
      id: "edit",
      label: "Edit",
      icon: PencilIcon,
      onClick: model => onEditModel(model!),
      showIf: model => model?.provider_name !== HUGGINGFACE_PROVIDER,
    },
  ];

  const contextMenuItems: ContextMenuItems<CustomModel>[] = [
    // {
    //   id: "view",
    //   label: "View model",
    //   icon: EyeIcon,
    //   onClick: onViewModel,
    // },
    {
      id: "edit",
      label: "Edit model",
      icon: PencilIcon,
      onClick: onEditModel,
      getDisabled: model => model.provider_name === HUGGINGFACE_PROVIDER,
      tooltipContent: "HuggingFace models cannot be edited",
    },
  ];

  return {
    columns,
    actions,
    contextMenuItems,
    isDeletingModel,
    onDeleteModel,
  };
};
